import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeftIcon, DownloadIcon, RefreshCwIcon, CreditCardIcon, UserIcon, BuildingIcon, CalendarIcon, DollarSignIcon, FileTextIcon } from 'lucide-react';
import { Sale } from '../types/Transaction';
export function SaleDetail() {
  const navigate = useNavigate();
  const {
    id
  } = useParams();
  // Mock data - in a real app, this would be fetched from an API
  const sale: Sale = {
    id: id || '1',
    transactionId: 'TXN-2024-001234',
    amount: 15000,
    currency: 'LKR',
    status: 'completed',
    source: 'payment_link',
    date: '2024-01-15T10:30:00',
    customerName: 'Nimal Perera',
    customerEmail: 'nimal@example.com',
    customerPhone: '+60 12 345 6789',
    paymentMethod: {
      type: 'card',
      last4: '4242',
      brand: 'Visa'
    },
    merchantName: 'ABC Store',
    merchantId: 'MERCH-001',
    description: 'Online purchase',
    fee: 450,
    netAmount: 14550,
    settlementId: 'SETTLE-2024-001',
    lifecycle: [{
      id: '1',
      timestamp: '2024-01-15T10:30:00',
      status: 'initiated',
      description: 'Transaction initiated by customer',
      metadata: {
        ip_address: '192.168.1.1',
        user_agent: 'Mozilla/5.0'
      }
    }, {
      id: '2',
      timestamp: '2024-01-15T10:30:15',
      status: 'processing',
      description: 'Payment processing with acquiring bank',
      metadata: {
        bank: 'Maybank',
        auth_code: 'AUTH123456'
      }
    }, {
      id: '3',
      timestamp: '2024-01-15T10:30:45',
      status: 'completed',
      description: 'Payment successful - funds captured',
      metadata: {
        settlement_date: '2024-01-17',
        rrn: 'RRN123456789'
      }
    }]
  };
  const getStatusBadge = (status: string) => {
    const styles: Record<string, string> = {
      initiated: 'bg-blue-100 text-blue-800',
      pending: 'bg-yellow-100 text-yellow-800',
      processing: 'bg-blue-100 text-blue-800',
      completed: 'bg-green-100 text-green-800',
      failed: 'bg-red-100 text-red-800',
      refunded: 'bg-gray-100 text-gray-800',
      refund_initiated: 'bg-orange-100 text-orange-800'
    };
    return <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${styles[status] || 'bg-gray-100 text-gray-800'}`} data-id="element-8264">
        {status.charAt(0).toUpperCase() + status.slice(1).replace('_', ' ')}
      </span>;
  };
  return <div className="space-y-6" data-id="element-8265">
      {/* Header */}
      <div className="flex items-center justify-between" data-id="element-8266">
        <button onClick={() => navigate('/sales')} className="flex items-center gap-2 text-gray-600 hover:text-gray-900" data-id="element-8267">
          <ArrowLeftIcon size={20} data-id="element-8268" />
          Back to Sales
        </button>
        <div className="flex items-center gap-3" data-id="element-8269">
          <button className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50" data-id="element-8270">
            <DownloadIcon size={18} data-id="element-8271" />
            Download Receipt
          </button>
          {sale.status === 'completed' && <button className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700" data-id="element-8272">
              <RefreshCwIcon size={18} data-id="element-8273" />
              Refund
            </button>}
        </div>
      </div>
      {/* Transaction Overview */}
      <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-8274">
        <div className="flex items-start justify-between mb-6" data-id="element-8275">
          <div data-id="element-8276">
            <h1 className="text-2xl font-bold text-gray-900" data-id="element-8277">
              {sale.transactionId}
            </h1>
            <p className="text-gray-500 mt-1" data-id="element-8278">{sale.description}</p>
          </div>
          {getStatusBadge(sale.status)}
        </div>
        {/* Amount Summary */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6 pb-6 border-b border-gray-200" data-id="element-8279">
          <div data-id="element-8280">
            <div className="text-sm text-gray-500" data-id="element-8281">Transaction Amount</div>
            <div className="mt-1 text-2xl font-semibold text-gray-900" data-id="element-8282">
              RM {sale.amount.toLocaleString('en-MY')}
            </div>
          </div>
          <div data-id="element-8283">
            <div className="text-sm text-gray-500" data-id="element-8284">Processing Fee</div>
            <div className="mt-1 text-2xl font-semibold text-gray-900" data-id="element-8285">
              RM {sale.fee.toLocaleString('en-MY')}
            </div>
          </div>
          <div data-id="element-8286">
            <div className="text-sm text-gray-500" data-id="element-8287">Net Amount</div>
            <div className="mt-1 text-2xl font-semibold text-green-600" data-id="element-8288">
              RM {sale.netAmount.toLocaleString('en-MY')}
            </div>
          </div>
          <div data-id="element-8289">
            <div className="text-sm text-gray-500" data-id="element-8290">Settlement ID</div>
            <div className="mt-1 text-sm font-medium text-blue-600" data-id="element-8291">
              {sale.settlementId ? <button onClick={() => navigate(`/settlement-detail/${sale.settlementId}`)} className="hover:underline" data-id="element-8292">
                  {sale.settlementId}
                </button> : <span className="text-gray-400" data-id="element-8293">Pending</span>}
            </div>
          </div>
        </div>
        {/* Transaction Details Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-8294">
          {/* Customer Information */}
          <div className="space-y-4" data-id="element-8295">
            <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2" data-id="element-8296">
              <UserIcon size={20} className="text-blue-600" data-id="element-8297" />
              Customer Information
            </h3>
            <div className="space-y-3" data-id="element-8298">
              <div data-id="element-8299">
                <div className="text-sm text-gray-500" data-id="element-8300">Name</div>
                <div className="text-sm font-medium text-gray-900" data-id="element-8301">
                  {sale.customerName}
                </div>
              </div>
              <div data-id="element-8302">
                <div className="text-sm text-gray-500" data-id="element-8303">Email</div>
                <div className="text-sm font-medium text-gray-900" data-id="element-8304">
                  {sale.customerEmail}
                </div>
              </div>
              {sale.customerPhone && <div data-id="element-8305">
                  <div className="text-sm text-gray-500" data-id="element-8306">Phone</div>
                  <div className="text-sm font-medium text-gray-900" data-id="element-8307">
                    {sale.customerPhone}
                  </div>
                </div>}
            </div>
          </div>
          {/* Payment Information */}
          <div className="space-y-4" data-id="element-8308">
            <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2" data-id="element-8309">
              <CreditCardIcon size={20} className="text-blue-600" data-id="element-8310" />
              Payment Information
            </h3>
            <div className="space-y-3" data-id="element-8311">
              <div data-id="element-8312">
                <div className="text-sm text-gray-500" data-id="element-8313">Payment Method</div>
                <div className="text-sm font-medium text-gray-900" data-id="element-8314">
                  {sale.paymentMethod.brand || sale.paymentMethod.type}{' '}
                  {sale.paymentMethod.last4 && `•••• ${sale.paymentMethod.last4}`}
                  {sale.paymentMethod.details && sale.paymentMethod.details}
                </div>
              </div>
              <div data-id="element-8315">
                <div className="text-sm text-gray-500" data-id="element-8316">Transaction Date</div>
                <div className="text-sm font-medium text-gray-900" data-id="element-8317">
                  {new Date(sale.date).toLocaleString('en-GB', {
                  dateStyle: 'long',
                  timeStyle: 'short'
                })}
                </div>
              </div>
              <div data-id="element-8318">
                <div className="text-sm text-gray-500" data-id="element-8319">Source</div>
                <div className="text-sm font-medium text-gray-900" data-id="element-8320">
                  {sale.source.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
                </div>
              </div>
            </div>
          </div>
          {/* Merchant Information */}
          <div className="space-y-4" data-id="element-8321">
            <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2" data-id="element-8322">
              <BuildingIcon size={20} className="text-blue-600" data-id="element-8323" />
              Merchant Information
            </h3>
            <div className="space-y-3" data-id="element-8324">
              <div data-id="element-8325">
                <div className="text-sm text-gray-500" data-id="element-8326">Merchant Name</div>
                <div className="text-sm font-medium text-gray-900" data-id="element-8327">
                  {sale.merchantName}
                </div>
              </div>
              <div data-id="element-8328">
                <div className="text-sm text-gray-500" data-id="element-8329">Merchant ID</div>
                <div className="text-sm font-medium text-gray-900" data-id="element-8330">
                  {sale.merchantId}
                </div>
              </div>
            </div>
          </div>
          {/* Additional Details */}
          {sale.invoiceId && <div className="space-y-4" data-id="element-8331">
              <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2" data-id="element-8332">
                <FileTextIcon size={20} className="text-blue-600" data-id="element-8333" />
                Related Documents
              </h3>
              <div className="space-y-3" data-id="element-8334">
                <div data-id="element-8335">
                  <div className="text-sm text-gray-500" data-id="element-8336">Invoice</div>
                  <button onClick={() => navigate(`/invoice-detail/${sale.invoiceId}`)} className="text-sm font-medium text-blue-600 hover:underline" data-id="element-8337">
                    {sale.invoiceId}
                  </button>
                </div>
              </div>
            </div>}
        </div>
      </div>
      {/* Transaction Lifecycle */}
      <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-8338">
        <h2 className="text-lg font-medium text-gray-900 mb-6" data-id="element-8339">
          Transaction Lifecycle
        </h2>
        <div className="space-y-6" data-id="element-8340">
          {sale.lifecycle.map((event, index) => <div key={event.id} className="relative" data-id="element-8341">
              {index !== sale.lifecycle.length - 1 && <div className="absolute left-4 top-10 bottom-0 w-0.5 bg-gray-200" data-id="element-8342"></div>}
              <div className="flex items-start gap-4" data-id="element-8343">
                <div className="flex-shrink-0" data-id="element-8344">
                  <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center" data-id="element-8345">
                    <div className="w-3 h-3 bg-blue-600 rounded-full" data-id="element-8346"></div>
                  </div>
                </div>
                <div className="flex-1 min-w-0" data-id="element-8347">
                  <div className="flex items-center justify-between" data-id="element-8348">
                    <div className="flex items-center gap-3" data-id="element-8349">
                      <p className="text-sm font-medium text-gray-900" data-id="element-8350">
                        {event.description}
                      </p>
                      {getStatusBadge(event.status)}
                    </div>
                    <p className="text-sm text-gray-500" data-id="element-8351">
                      {new Date(event.timestamp).toLocaleString('en-GB', {
                    dateStyle: 'short',
                    timeStyle: 'short'
                  })}
                    </p>
                  </div>
                  {event.metadata && <div className="mt-2 p-3 bg-gray-50 rounded-lg" data-id="element-8352">
                      <div className="grid grid-cols-2 gap-2" data-id="element-8353">
                        {Object.entries(event.metadata).map(([key, value]) => <div key={key} data-id="element-8354">
                            <span className="text-xs text-gray-500" data-id="element-8355">
                              {key.replace('_', ' ').toUpperCase()}:
                            </span>
                            <span className="text-xs font-medium text-gray-900 ml-1" data-id="element-8356">
                              {String(value)}
                            </span>
                          </div>)}
                      </div>
                    </div>}
                </div>
              </div>
            </div>)}
        </div>
      </div>
    </div>;
}